// client/src/components/AffiliatesDocsViewer.js

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { storage } from '../../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import {
  FaArrowLeft,
  FaLock,
  FaShareAlt,
  FaDownload,
} from 'react-icons/fa';

const IMG_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
const PDF_EXT = ['pdf'];

const getExt = (nameOrUrl = '') => {
  const lower = nameOrUrl.toLowerCase().split('?')[0];
  const parts = lower.split('.');
  return parts.length > 1 ? parts.pop() : '';
};
const isImage = (ext) => IMG_EXT.includes(ext);
const isPdf = (ext) => PDF_EXT.includes(ext);

// pour fabriquer des noms propres pour chaque fichier si multiple
const basenameNoExt = (s = '') => s.replace(/\.[^/.]+$/, '');
const getPathExt = (p = '') => {
  const parts = p.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : 'png';
};

const AffiliatesDocsViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Données passées via navigate('/affiliatesdocsviewer', { state: payload })
  const navState = location.state;

  // Fallback si l'utilisateur rafraîchit la page : on relit du sessionStorage
  const persisted = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('affdocs_view') || 'null');
    } catch {
      return null;
    }
  }, []);

  const { name = 'Document', scope = 'public', paths = [] } = navState || persisted || {};
  const ext = useMemo(() => getExt(name), [name]);

  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!paths.length) {
      setLoading(false);
      setErr('Chemin introuvable ou accès direct non autorisé.');
      return;
    }
    setLoading(true);
    Promise.all(paths.map((p) => getDownloadURL(ref(storage, p))))
      .then((u) => {
        setUrls(u);
        setErr(null);
      })
      .catch((e) => {
        setUrls([]);
        setErr(e?.message || 'Erreur de chargement.');
      })
      .finally(() => setLoading(false));
  }, [paths]);

  const singleUrl = urls[0] || null;
  const isMulti = urls.length > 1;

  const handleBack = () => navigate(-1);

  // Téléchargement "réel" (force download) pour 1 ou N fichiers
  const downloadBlob = async (url, filename) => {
    const resp = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!resp.ok) throw new Error('Download failed');
    const blob = await resp.blob();
    const a = document.createElement('a');
    const dlUrl = URL.createObjectURL(blob);
    a.href = dlUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(dlUrl);
  };

  const handleDownloadSingle = async () => {
    if (!singleUrl) return;
    const base = basenameNoExt(name) || 'document';
    const fileExt = ext || getPathExt(paths[0]) || 'png';
    const filename = `${base}.${fileExt}`;
    try {
      await downloadBlob(singleUrl, filename);
    } catch (e) {
      console.error('Download error:', e);
      alert('Échec du téléchargement.');
    }
  };

  // Télécharger CHAQUE page séparément (pas de fusion)
  const handleDownloadAllSeparate = async () => {
    const base = basenameNoExt(name) || 'document';
    const exts = paths.map(getPathExt);

    for (let i = 0; i < urls.length; i++) {
      const u = urls[i];
      const idx = i + 1;
      const fileExt = exts[i] || ext || 'png';
      const filename = urls.length === 1 ? `${base}.${fileExt}` : `${base}_page${idx}.${fileExt}`;
      try {
        await downloadBlob(u, filename);
      } catch (e) {
        console.error('Download failed for', u, e);
        alert(`Échec du téléchargement de la page ${idx}.`);
      }
    }
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: 'var(--eb-bg)' }}>
      <style>{`
        .adv-wrap { max-width: var(--eb-content-max); margin: 0 auto; padding: 24px 16px 56px; }
        .adv-topbar { display: flex; align-items: center; justify-content: space-between; gap: 12px 16px; margin-bottom: 20px; flex-wrap: wrap; }
        .adv-titlebox { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; min-width: 0; flex: 1 1 260px; }
        .adv-title { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: var(--eb-text); word-break: break-word; }
        .adv-namepath { color: var(--eb-muted); font-size: 13px; margin-top: 2px; }
        .adv-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
        .adv-stage { border: 1px solid var(--eb-border); background: var(--eb-surface); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); overflow: hidden; }

        /* Multi-images container */
        .adv-stack { width: 100%; display: flex; flex-direction: column; gap: 16px; padding: 16px; background: var(--eb-surface-2); }
        .adv-imgpage {
          width: 100%;
          height: 76vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: auto;
          background: var(--eb-surface);
          border: 1px solid var(--eb-border);
          border-radius: var(--eb-radius);
        }
        .adv-imgpage img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }

        /* Image unique */
        .adv-imgbox { width: 100%; height: 76vh; display: flex; align-items: center; justify-content: center; overflow: auto; background: var(--eb-surface-2); }
        .adv-imgbox img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }

        /* PDF */
        .adv-pdf { width: 100%; height: 76vh; border: 0; background: var(--eb-surface); display: block; }

        .adv-fallback { padding: 48px 24px; text-align: center; color: var(--eb-muted); font-size: 14.5px; display: flex; flex-direction: column; align-items: center; gap: 14px; }
        @media (max-width: 640px) {
          .adv-wrap { padding: 16px 12px 40px; }
          .adv-imgpage, .adv-imgbox, .adv-pdf { height: 64vh; }
          .adv-titlebox { order: 3; flex-basis: 100%; }
        }
      `}</style>

      <div className="adv-wrap">
        <div className="adv-topbar">
          <button className="eb-btn eb-btn--secondary" onClick={handleBack}>
            <FaArrowLeft aria-hidden="true" /> Retour
          </button>

          <div className="adv-titlebox">
            <div>
              <div className="adv-title">{name}</div>
              <div className="adv-namepath">{ext ? ext.toUpperCase() : 'FICHIER'}</div>
            </div>
            {scope === 'confidential'
              ? <span className="eb-badge eb-badge--danger"><FaLock aria-hidden="true" /> Confidentiel</span>
              : <span className="eb-badge"><FaShareAlt aria-hidden="true" /> À partager</span>}
          </div>

          <div className="adv-actions">
            {!isMulti ? (
              <button className="eb-btn eb-btn--primary" onClick={handleDownloadSingle} disabled={!singleUrl}>
                <FaDownload aria-hidden="true" /> Télécharger
              </button>
            ) : (
              <button className="eb-btn eb-btn--primary" onClick={handleDownloadAllSeparate} disabled={!urls.length}>
                <FaDownload aria-hidden="true" /> Télécharger
              </button>
            )}
          </div>
        </div>

        <div className="adv-stage">
          {loading ? (
            <div className="adv-fallback"><span className="eb-spinner" aria-hidden="true" />Chargement…</div>
          ) : err ? (
            <div className="adv-fallback" style={{ color: 'var(--eb-danger)' }}>Erreur : {err}</div>
          ) : !urls.length ? (
            <div className="adv-fallback">Aucune ressource à afficher.</div>
          ) : isMulti && isImage(ext) ? (
            <div className="adv-stack">
              {urls.map((u, i) => (
                <div key={i} className="adv-imgpage">
                  <img src={u} alt={`${name} - ${i + 1}`} crossOrigin="anonymous" />
                </div>
              ))}
            </div>
          ) : !isMulti && isImage(ext) ? (
            <div className="adv-imgbox">
              <img src={singleUrl} alt={name || 'document'} crossOrigin="anonymous" />
            </div>
          ) : !isMulti && isPdf(ext) ? (
            <iframe className="adv-pdf" src={singleUrl} title={name || 'document pdf'} />
          ) : (
            <div className="adv-fallback">
              Aperçu non supporté pour ce type de fichier.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AffiliatesDocsViewer;
